/*
  RD PRODUCT PAGE SECTIONS JS
  Small, safe behaviors used by the standalone RD product sections:
  - FAQ accordion
  - Complete The Look quick-add
  - Optional customer-gallery dot syncing on horizontal mobile scroll

  No loader delays, no timeout-based animation, no dependency on non-RD theme code.
*/

(() => {
  const SELECTORS = {
    faqItem: '[data-rd-pdp-faq-item]',
    faqButton: '[data-rd-pdp-faq-toggle]',
    quickAdd: '[data-rd-pdp-quick-add]',
    lovedTrack: '[data-rd-pdp-loved-track]',
    lovedDot: '[data-rd-pdp-loved-dot]'
  };

  const moneyMessage = (message) => {
    window.dispatchEvent(new CustomEvent('rd:pdp:notice', { detail: { message } }));
  };

  const getRoutes = () => {
    return {
      cartAdd: (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js',
      cart: (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart'
    };
  };

  const setButtonState = (button, state) => {
    if (!button) return;
    const defaultText = button.dataset.defaultText || button.textContent.trim();
    button.dataset.defaultText = defaultText;

    if (state === 'loading') {
      button.disabled = true;
      button.textContent = button.dataset.loadingText || 'Adding...';
      return;
    }

    if (state === 'added') {
      button.disabled = false;
      button.textContent = button.dataset.addedText || 'Added';
      window.setTimeout(() => {
        button.textContent = defaultText;
      }, 1200);
      return;
    }

    button.disabled = false;
    button.textContent = defaultText;
  };

  const quickAdd = async (button) => {
    const variantId = button && button.dataset.rdPdpQuickAdd;
    if (!variantId) return;

    setButtonState(button, 'loading');

    try {
      const routes = getRoutes();

      const response = await fetch(routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Could not add item.');
      }

      window.dispatchEvent(new CustomEvent('cart:refresh'));
      window.dispatchEvent(new CustomEvent('rd:cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      setButtonState(button, 'added');
      moneyMessage('Added to cart');
    } catch (error) {
      setButtonState(button, 'idle');
      moneyMessage('Unable to add. Please try again.');
    }
  };

  const initFaq = (root = document) => {
    root.querySelectorAll(SELECTORS.faqButton).forEach((button) => {
      if (button.dataset.rdPdpFaqReady === 'true') return;
      button.dataset.rdPdpFaqReady = 'true';

      button.addEventListener('click', () => {
        const item = button.closest(SELECTORS.faqItem);
        const answerId = button.getAttribute('aria-controls');
        const answer = answerId ? document.getElementById(answerId) : null;
        const isOpen = item && item.classList.contains('is-open');

        if (item) item.classList.toggle('is-open', !isOpen);
        button.setAttribute('aria-expanded', String(!isOpen));
        if (answer) answer.hidden = isOpen;
      });
    });
  };

  const initQuickAdd = (root = document) => {
    root.querySelectorAll(SELECTORS.quickAdd).forEach((button) => {
      if (button.dataset.rdPdpQuickAddReady === 'true') return;
      button.dataset.rdPdpQuickAddReady = 'true';

      button.addEventListener('click', () => quickAdd(button));
    });
  };

  const initLovedGallery = (root = document) => {
    root.querySelectorAll(SELECTORS.lovedTrack).forEach((track) => {
      if (track.dataset.rdPdpLovedReady === 'true') return;
      track.dataset.rdPdpLovedReady = 'true';

      const section = track.closest('[data-rd-pdp-loved]');
      const dots = section ? Array.from(section.querySelectorAll(SELECTORS.lovedDot)) : [];
      if (!dots.length) return;

      const updateDots = () => {
        const items = Array.from(track.children);
        if (!items.length) return;

        const center = track.scrollLeft + track.clientWidth / 2;
        let activeIndex = 0;
        let activeDistance = Infinity;

        items.forEach((item, index) => {
          const itemCenter = item.offsetLeft + item.offsetWidth / 2;
          const distance = Math.abs(center - itemCenter);
          if (distance < activeDistance) {
            activeIndex = index;
            activeDistance = distance;
          }
        });

        dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === activeIndex);
        });
      };

      track.addEventListener('scroll', updateDots, { passive: true });
      updateDots();
    });
  };

  const init = (root = document) => {
    initFaq(root);
    initQuickAdd(root);
    initLovedGallery(root);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
